import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import SocialLoginButtons from '../components/SocialLoginButtons';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(email, password, needs2FA ? totpCode : undefined);
      if (data?.requires_2fa) {
        setNeeds2FA(true);
        return;
      }
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'linear-gradient(135deg, #FAFAFA 0%, #FFF7ED 100%)'}}>
      <Card className="w-full max-w-md bg-white border-[#E5E5E5] shadow-2xl" data-testid="login-card">
        <CardHeader className="space-y-2">
          <CardTitle className="text-3xl font-heading font-bold text-[#0A0A0A]">Bienvenido a Mi Tribu</CardTitle>
          <CardDescription className="text-[#737373]">Inicia sesión para descubrir eventos</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0A0A0A]">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white border-[#E5E5E5] text-[#0A0A0A] focus:border-primary"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#0A0A0A]">Contraseña</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white border-[#E5E5E5] text-[#0A0A0A] focus:border-primary"
                data-testid="password-input"
              />
            </div>
            {needs2FA && (
              <div className="space-y-2 p-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg" data-testid="totp-section">
                <Label htmlFor="totp" className="text-[#0A0A0A] font-semibold">Código de verificación (2FA)</Label>
                <p className="text-xs text-[#737373]">Tu cuenta tiene verificación en dos pasos activada. Ingresá el código de 6 dígitos de tu app de autenticación.</p>
                <Input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                  className="bg-white border-[#E5E5E5] text-[#0A0A0A] focus:border-primary text-center text-lg tracking-widest"
                  data-testid="totp-code-input"
                />
              </div>
            )}
            {error && (
              <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="error-message">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
              data-testid="login-button"
            >
              {loading ? 'Iniciando...' : 'Iniciar Sesión'}
            </Button>
          </form>

          <SocialLoginButtons />

          <p className="text-center text-sm text-[#737373]">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
              Regístrate
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
